import { defineComponent as _defineComponent } from 'vue'
import { ComponentPublicInstance } from 'vue';
import { computed, onMounted, onUnmounted, ref, toRefs, useContext, watch } from '@nuxtjs/composition-api';
import { Metaverses } from '~/core/constants/affility/global/Metaverses';
import type BaseLinkMenu from '~/core/components/base/link/Menu.vue';
import type ComposedListMetaverses from '~/core/components/composed/card/Metaverse.vue';
import { ACTIONS as SHOP_ACTIONS } from '~/store/affility/pages/shop/names';

interface Props {
    trigger?: 'click' | 'hover' | 'focus' | 'manual',
    showTitle?: boolean,
    value?: boolean,
    width?: number | string,
    listenKeys?: boolean,
}

type MetaverseButton = ComponentPublicInstance<typeof BaseLinkMenu> & { $el: HTMLButtonElement };
type MetaverseList = ComponentPublicInstance<typeof ComposedListMetaverses> & { metaverseButtons: MetaverseButton[] | null };

export default /*#__PURE__*/_defineComponent({
  __name: 'Metaverse',
  props: {
    trigger: null,
    showTitle: { type: Boolean, default: true },
    value: { type: Boolean },
    width: { default: 240 },
    listenKeys: { type: Boolean, default: true }
  },
  emits: ['input', 'open', 'close', 'metaverse-switched'],
  setup(__props: any, { emit: $emit }) {

const props = __props as { trigger?: 'click' | 'hover' | 'focus' | 'manual', showTitle: boolean, value?: boolean, width: number | string, listenKeys: boolean };




const {
    trigger,
    value,
    showTitle,
    width,
    listenKeys,
} = toRefs(props);

const {
    store,
    $setMetaverseId,
} = useContext() as ReturnType<typeof useContext> & {
    $setMetaverseId: (id: number) => void,
};

const currentMetaverseId = computed(() => store.getters['map/mapModule/metaverseId']);
const currentMetaverse = computed(() => Metaverses[currentMetaverseId.value]);
const availableMetaverses = computed(() => Object.values(Metaverses));
const maxIdLength = Math.max(...Object.values(Metaverses).map(metaverse => metaverse.id.toString().length));

const sortedMetaverses = computed(() => {
    return [currentMetaverse.value].concat(availableMetaverses.value.filter(metaverse => metaverse !== currentMetaverse.value));
});

const isOpen = ref(value.value);
watch(value, (val) => isOpen.value = val);
watch(isOpen, (val) => {
    $emit('input', val);
    isCapturingId = val;
    if (val) {
        $emit('open');
    } else {
        $emit('close');
        capturedSequence = '';
        if (document.activeElement && metaverseList.value?.$el.contains(document.activeElement)) {
            (document.activeElement as HTMLButtonElement)?.blur?.();
        }
    }
});

const switchMetaverse = (metaverse: typeof Metaverses[keyof typeof Metaverses]) => {
    $setMetaverseId(metaverse.id);
    $emit('metaverse-switched', metaverse);
    isOpen.value = false;
    store.dispatch(`affility/pages/shop/${SHOP_ACTIONS.ON_CLEAR_SHOP_DATA}`);
};

const metaverseList = ref<MetaverseList | null>(null);
let isCapturingId = false;
let capturedSequence = '';
const windowKeypressHandler = (event: KeyboardEvent) => {
    const initialFocusedElement = document.activeElement as HTMLElement;
    if (initialFocusedElement?.tagName === 'INPUT' ||
        initialFocusedElement?.tagName === 'TEXTAREA' ||
        initialFocusedElement?.isContentEditable) {
        return;
    }
    if (capturedSequence.length >= maxIdLength) {
        capturedSequence = '';
    }
    if (event.key === '/') {
        isOpen.value = !isOpen.value;
    }
    if (event.key === 'Escape') {
        isOpen.value = false;
    }
    isCapturingId = isOpen.value;
    if (isCapturingId) {
        const buttons = metaverseList.value.metaverseButtons;
        if (!buttons) {
            return;
        }
        let itemToFocus: MetaverseButton | null = null;
        if (event.key.match(/[0-9]/)) {
            capturedSequence += event.key;
            itemToFocus = buttons
                ?.find(button => {
                    const metaverseId = button.$el.attributes.getNamedItem('metaverse-id');
                    return metaverseId?.value.startsWith(capturedSequence);
                }) as MetaverseButton | null;
        }
        if (event.key.startsWith('Arrow') || event.key === 'Tab') {
            if (event.key === 'Tab') {
                event.preventDefault();
            }
            const activeIndex = buttons?.findIndex(button => button.$el === document.activeElement);
            let indexToFocus = 0;
            if (event.key === 'ArrowUp') {
                indexToFocus = (activeIndex! - 1 + buttons.length) % buttons.length;
            } else if (event.key === 'ArrowDown' || event.key === 'Tab') {
                indexToFocus = (activeIndex! + 1) % buttons.length;
            } else if (event.key === 'ArrowLeft') {
                indexToFocus = 0;
            } else if (event.key === 'ArrowRight') {
                indexToFocus = buttons.length - 1;
            }
            itemToFocus = buttons?.[indexToFocus];
        }
        if (!itemToFocus) {
            itemToFocus = buttons?.[1];
        }
        (itemToFocus?.$el as HTMLButtonElement).focus();
    }
};

const registerListeners = () => {
    if (listenKeys.value) {
        window.addEventListener('keydown', windowKeypressHandler);
    }
};

const unregisterListeners = () => {
    window.removeEventListener('keydown', windowKeypressHandler);
};

watch(listenKeys, (val, oldVal) => {
    if (val) {
        registerListeners();
    } else if (!val && oldVal) {
        unregisterListeners();
    }
});

onMounted(() => {
    registerListeners();
});

onUnmounted(() => {
    unregisterListeners();
});

return { __sfc: true,$emit, props, trigger, value, showTitle, width, listenKeys, store, $setMetaverseId, currentMetaverseId, currentMetaverse, availableMetaverses, maxIdLength, sortedMetaverses, isOpen, switchMetaverse, metaverseList, isCapturingId, capturedSequence, windowKeypressHandler, registerListeners, unregisterListeners }
}

})